import { useLayoutEffect, useState } from 'react';

/**
 * La taille dessinée de chaque bulle de la carte, mesurée à l'écran (ticket 25).
 *
 * Le serveur place les bulles d'après une hauteur ESTIMÉE sur le titre (`hauteurDUneBulle`),
 * volontairement large : assez pour qu'elles ne se chevauchent pas. Mais un trait qui doit
 * s'arrêter au bord d'une bulle, lui, demande son vrai bord : estimé trop haut, il
 * s'arrêterait dans le vide ; trop bas, il rayerait la première ou la dernière ligne.
 *
 * La mesure suit le texte : un titre renommé, une police qui finit de charger, et la
 * bulle est remesurée (`ResizeObserver`, qui lit la boîte avant la caméra : l'échelle ne
 * change rien). Rien n'est stocké : c'est une lecture de l'écran, refaite à chaque ouverture.
 */
export interface TailleDessinee {
  largeur: number;
  hauteur: number;
}

/**
 * `monde` : l'élément qui contient les nœuds (`[data-bulle]`), une fois dans la page. `cle`
 * change quand les bulles de la page changent : les nœuds sont alors réobservés.
 */
export function useTaillesDesBulles(
  monde: HTMLElement | null,
  cle: string
): Record<string, TailleDessinee> {
  const [tailles, setTailles] = useState<Record<string, TailleDessinee>>({});

  useLayoutEffect(() => {
    if (!monde) return;
    const observateur = new ResizeObserver((entrees) => {
      setTailles((avant) => {
        let apres: Record<string, TailleDessinee> | null = null;
        for (const { target } of entrees) {
          const noeud = target as HTMLElement;
          const id = noeud.dataset.bulle;
          const taille = { largeur: noeud.offsetWidth, hauteur: noeud.offsetHeight };
          if (!id || taille.hauteur === 0) continue;
          const connue = avant[id];
          if (connue && connue.largeur === taille.largeur && connue.hauteur === taille.hauteur) continue;
          apres ??= { ...avant };
          apres[id] = taille;
        }
        return apres ?? avant;
      });
    });
    for (const noeud of monde.querySelectorAll<HTMLElement>('[data-bulle]')) {
      observateur.observe(noeud);
    }
    return () => observateur.disconnect();
  }, [monde, cle]);

  return tailles;
}
